import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetHolidayPayEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { getTaxWeekTaxYear, showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { ColumnsType } from 'antd/lib/table';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type HistoryHolidayPayModalProps = {
  isOpen: boolean;
  toggle: () => void;
  employeeId: string;
  // Match umbrella-fe ModalHoliday: employer cost columns (ERNI, App Levy,
  // Employer's Pension) only show for umbrella-style workers.
  paymentModel?: string;
};

const HistoryHolidayPayModal: React.FC<HistoryHolidayPayModalProps> = ({
  isOpen,
  toggle,
  employeeId,
  paymentModel,
}: HistoryHolidayPayModalProps) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const dispatch = useDispatch<AppDispatch>();
  const { holidayList } = useSelector((state: AppState) => state.employeeStore);
  const { loading, list, pagination, queryParams } = holidayList;
  const [taxYear, setTaxYear] = useState<number>(
    getTaxWeekTaxYear().taxYear,
  );

  useEffect(() => {
    if (isOpen) {
      handleGetHolidayPayEmployeeList({ page: 0, size: 20, taxYear });
    }
  }, [isOpen, taxYear]);

  const handleGetHolidayPayEmployeeList = async (
    params: GetHolidayPayEmployeeListParams,
  ) => {
    const newParams = {
      ...params,
      id: employeeId,
    };
    dispatch(employeeActions.getHolidayPayEmployeeList(newParams));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;

    handleGetHolidayPayEmployeeList({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const showEmployerCosts =
    paymentModel === PAYMENT_MODEL_ENUM.UMBRELLA ||
    paymentModel === PAYMENT_MODEL_ENUM.HYBRID;

  const renderMoney = (value: number) => (
    <p>{formatMoney(value || 0, showCurrencyByCountry(payrollCountry))}</p>
  );

  const columns: ColumnsType<any> = [
    {
      key: 'taxPeriod',
      title: messages['common.taxPeriod'],
      render: (_: unknown, record: any) => {
        const year = record?.payroll?.taxYear ?? record?.taxYear;
        return year ? `${record?.taxPeriod}/${year}` : (record?.taxPeriod ?? '-');
      },
    },
    {
      key: 'openingBalance',
      title: messages['common.openingBalance'],
      dataIndex: 'openingBalance',
      render: renderMoney,
    },
    {
      key: 'accrued',
      title: messages['common.accrued'],
      dataIndex: 'accrued',
      render: renderMoney,
    },
    {
      key: 'paid',
      title: messages['common.paid'],
      dataIndex: 'paid',
      render: renderMoney,
    },
    {
      key: 'balance',
      title: messages['common.balance'],
      dataIndex: 'balance',
      render: renderMoney,
    },
    // Employer cost columns — only for umbrella/hybrid, matching umbrella-fe
    ...(showEmployerCosts
      ? [
          {
            key: 'ERNI',
            title: messages['common.ERNI'],
            dataIndex: 'ERNI',
            render: renderMoney,
          },
          {
            key: 'appLevy',
            title: messages['common.appLevy'],
            dataIndex: 'appLevy',
            render: renderMoney,
          },
          {
            key: 'ERPension',
            title: messages['common.emprPension'],
            dataIndex: 'ERPension',
            render: renderMoney,
          },
        ]
      : []),
  ];

  return (
    <AppModalContainer
      title={messages['common.historyHolidayPay']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.close'],
          variant: 'outlined',
          onClick: toggle,
        },
      ]}
      width="65vw"
    >
      <div className="flex justify-end mb-2 px-4">
        <TaxYearSelect
          initValue={taxYear}
          onFilter={(value) => setTaxYear(value.taxYear ?? getTaxWeekTaxYear().taxYear)}
        />
      </div>
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1200 }}
          columns={columns}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default HistoryHolidayPayModal;
